import { skipToken } from "@reduxjs/toolkit/query";
import {
  resolvePageContextGrounding,
  type PageContextDescriptor,
} from "@repo/app-types/agents/pageContext";
import {
  ChatContextPill,
  chatContextPillIconClassName,
  chatContextPillMarkClassName,
} from "@repo/ui/chat/chatContextPill";
import { skeletonStyle } from "@repo/ui/Skeletons/skeletons";
import { IconBook2, IconUserBolt } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import { useAllMinimalPlaybooksQuery } from "../../api/entities/playbook.js";
import { useGetTeammateAgentQuery } from "../../api/entities/teammateAgent.js";
import type { ResearchAgentThreadMetadata } from "./researchAgentContext.js";
import { useResearchAgentPageContext } from "./researchAgentPageContext.js";

export interface ResearchAgentContextPillProps {
  /**
   * The metadata of the selected thread. Company-scoped threads carry no scope
   * ids; legacy threads' ids ground the chat when the current page doesn't.
   */
  threadMetadata: ResearchAgentThreadMetadata;
}

/** Shown when the scoped entity is gone (deleted, or no longer visible). */
const UNKNOWN_TITLE = "Unknown";

/**
 * The shared precedence rule (page wins, legacy thread scope as fallback),
 * flattened to the one id the pill names.
 */
function resolveGrounding(
  pageContext: PageContextDescriptor | undefined,
  threadMetadata: ResearchAgentThreadMetadata,
): Pick<ResearchAgentThreadMetadata, "playbookId" | "teammateAgentId"> {
  const grounding = resolvePageContextGrounding(pageContext, threadMetadata);
  if (!grounding) {
    return {};
  }
  switch (grounding.page) {
    case "teammateAgent":
      return { teammateAgentId: grounding.teammateAgentId };
    // The signal editor grounds to a signal, which this pill (playbook / voice
    // agent only) does not surface.
    case "signalConfiguration":
      return {};
    default:
      return { playbookId: grounding.playbookId };
  }
}

/**
 * Names the playbook or voice agent the next message will be grounded to: the
 * resource on the page the user is viewing, or a legacy thread's stored scope
 * when the page carries none. Renders nothing for an ungrounded company-wide
 * chat.
 *
 * The grounding comes from the shared `resolvePageContextGrounding`, the same
 * precedence rule the agent applies server-side (see
 * `resolveResearchAgentPageContext` in `agent-api`).
 *
 * The name stands alone as the pill's title — the icon carries which kind of
 * thing it is, matching how the app already marks playbooks and voice agents
 * in navigation and search.
 */
export const ResearchAgentContextPill: FC<ResearchAgentContextPillProps> = ({
  threadMetadata,
}) => {
  const pageContext = useResearchAgentPageContext();
  const { teammateAgentId, playbookId } = resolveGrounding(
    pageContext,
    threadMetadata,
  );

  const { data: teammateAgent, isLoading: isTeammateAgentLoading } =
    useGetTeammateAgentQuery(teammateAgentId ?? skipToken);

  // The minimal list is fetched by the app anyway and covers every playbook,
  // including the ones behind voice agents; `playbookById` would pull a whole
  // assessment tree to read one name.
  const { data: playbooks, isLoading: arePlaybooksLoading } =
    useAllMinimalPlaybooksQuery(playbookId ? undefined : skipToken);

  if (teammateAgentId) {
    return (
      <ChatContextPill
        icon={
          <span className={chatContextPillMarkClassName}>
            <IconUserBolt
              aria-hidden
              className={chatContextPillIconClassName}
            />
          </span>
        }
        title={
          isTeammateAgentLoading ? (
            <ContextPillTitleSkeleton />
          ) : (
            teammateAgent?.name || UNKNOWN_TITLE
          )
        }
      />
    );
  }

  if (!playbookId) {
    return null;
  }

  const playbook = playbooks?.find((candidate) => candidate.id === playbookId);

  return (
    <ChatContextPill
      icon={
        <span className={chatContextPillMarkClassName}>
          <IconBook2 aria-hidden className={chatContextPillIconClassName} />
        </span>
      }
      title={
        arePlaybooksLoading ? (
          <ContextPillTitleSkeleton />
        ) : (
          playbook?.name || UNKNOWN_TITLE
        )
      }
    />
  );
};

/**
 * Holds the pill's line while the name loads. The pill sits above the composer
 * input, so rendering nothing until the name arrives would grow the composer
 * under the user's cursor.
 */
const ContextPillTitleSkeleton: FC = (): ReactNode => (
  <span className="flex h-4 items-center">
    <span className={skeletonStyle({ className: "h-2 w-24" })} />
    <span className="sr-only">Loading chat context...</span>
  </span>
);
